/**
 * Market identifier utilities for parsing and working with market symbols.
 *
 * Symbols follow the format:
 * - Main dex: "BTC", "ETH", "SOL"
 * - HIP-3 dexes: "xyz:NVDA", "flx:TSLA", "vntl:SOMETHING"
 *
 * @module marketId
 */

import type { DexId, Hip3DexId } from '@infinex/types';

/**
 * Array of all HIP-3 dex identifiers.
 * Add new dexes here as they become available.
 */
export const HIP3_DEX_IDS: Hip3DexId[] = ['xyz', 'flx', 'vntl'];

const DEX_PREFIXES = HIP3_DEX_IDS.map((dex) => [`${dex}:`, dex] as const);

/**
 * A parsed market symbol.
 *
 * `dex` is `null` when the symbol carries a prefix that is not one of the known
 * dexes. Hyperliquid mainnet lists builder-deployed dexes outside the static
 * {@link DexId} union (e.g. `km:`), so an unknown prefix is a normal live value,
 * not an error — it is reported as unknown rather than silently resolved to the
 * main dex. `baseAsset` keeps the full symbol in that case, since the prefix
 * cannot be established as a dex prefix.
 */
export type ParsedSymbol = {
  dex: DexId | null;
  baseAsset: string;
};

/**
 * Parse a symbol to extract its dex and base asset.
 *
 * @param symbol - The market symbol (e.g., "BTC", "xyz:NVDA")
 * @returns The dex identifier (`null` when the prefix is not a known dex) and base asset
 *
 * @example
 * parseSymbol("BTC")       // { dex: 'main', baseAsset: 'BTC' }
 * parseSymbol("xyz:NVDA")  // { dex: 'xyz', baseAsset: 'NVDA' }
 * parseSymbol("vntl:FOO")  // { dex: 'vntl', baseAsset: 'FOO' }
 * parseSymbol("km:FOO")    // { dex: null, baseAsset: 'km:FOO' }
 */
export function parseSymbol(symbol: string): ParsedSymbol {
  // Guard against undefined/null - return safe default
  if (!symbol) {
    return { dex: 'main', baseAsset: '' };
  }

  for (const [prefix, dex] of DEX_PREFIXES) {
    if (symbol.startsWith(prefix)) {
      return { dex, baseAsset: symbol.slice(prefix.length) };
    }
  }

  // A prefix that is not a known dex belongs to a dex this build cannot name.
  // Callers that need a concrete dex must choose their fallback explicitly.
  if (symbol.includes(':')) {
    return { dex: null, baseAsset: symbol };
  }

  return { dex: 'main', baseAsset: symbol };
}

/**
 * Get the display name for a market (just the base asset, no prefix).
 *
 * @param symbol - The market symbol
 * @returns The base asset name for display
 *
 * @example
 * getDisplayName("BTC")       // "BTC"
 * getDisplayName("xyz:NVDA")  // "NVDA"
 */
export function getDisplayName(symbol: string): string {
  return parseSymbol(symbol).baseAsset;
}

/**
 * Get the dex identifier from a symbol.
 *
 * @param symbol - The market symbol
 * @returns The dex identifier, or `null` when the prefix is not a known dex
 *
 * @example
 * getDex("BTC")       // 'main'
 * getDex("xyz:NVDA")  // 'xyz'
 * getDex("km:FOO")    // null
 */
export function getDex(symbol: string): DexId | null {
  return parseSymbol(symbol).dex;
}

/**
 * Check if a symbol requires a dex parameter for API calls.
 * Main dex markets don't need the parameter, HIP-3 markets do.
 *
 * @param symbol - The market symbol
 * @returns True if the dex parameter should be passed to API calls
 *
 * @example
 * needsDexParam("BTC")       // false
 * needsDexParam("xyz:NVDA")  // true
 */
export function needsDexParam(symbol: string): boolean {
  return isHip3Dex(parseSymbol(symbol).dex);
}

/**
 * Build a symbol from dex and base asset components.
 *
 * @param dex - The dex identifier
 * @param baseAsset - The base asset name
 * @returns The full symbol string
 *
 * @example
 * buildSymbol('main', 'BTC')  // "BTC"
 * buildSymbol('xyz', 'NVDA')  // "xyz:NVDA"
 */
export function buildSymbol(dex: DexId, baseAsset: string): string {
  if (dex === 'main') {
    return baseAsset;
  }
  return `${dex}:${baseAsset}`;
}

/**
 * Check if a symbol belongs to a specific dex.
 *
 * @param symbol - The market symbol
 * @param dex - The dex to check against
 * @returns True if the symbol belongs to the specified dex
 *
 * A symbol whose prefix is not a known dex belongs to no known dex, so it
 * matches nothing — including 'main'.
 *
 * @example
 * isFromDex("BTC", "main")       // true
 * isFromDex("xyz:NVDA", "xyz")   // true
 * isFromDex("xyz:NVDA", "main")  // false
 * isFromDex("km:FOO", "main")    // false
 */
export function isFromDex(symbol: string, dex: DexId): boolean {
  return parseSymbol(symbol).dex === dex;
}

/**
 * Get all known dex identifiers.
 * Useful for iterating over dexes when fetching market data.
 *
 * @returns Array of all dex identifiers
 */
export function getAllDexIds(): DexId[] {
  return ['main', ...getHip3DexIds()];
}

/**
 * HIP-3 dexes (builder-deployed perpetuals) that require separate clearinghouse queries.
 * Each dex has its own clearinghouse with separate positions and margin.
 *
 * @returns Array of HIP-3 dex identifiers (excludes 'main')
 */
export function getHip3DexIds(): Hip3DexId[] {
  return [...HIP3_DEX_IDS];
}

/**
 * Check if a dex identifier represents a HIP-3 market.
 * HIP-3 markets are builder-deployed perpetuals that require separate clearinghouse handling.
 *
 * An unknown dex (`null`, from a symbol prefix outside the static union) is not
 * a HIP-3 market this build can address: it has no clearinghouse to query, so
 * the HIP-3 request shape must not be used for it.
 *
 * @param dex - The dex identifier to check
 * @returns True if the dex is a HIP-3 market (not 'main' and not unknown)
 *
 * @example
 * isHip3Dex('main')  // false
 * isHip3Dex('xyz')   // true
 * isHip3Dex('flx')   // true
 * isHip3Dex(null)    // false
 */
export function isHip3Dex(dex: DexId | null): dex is Hip3DexId {
  return dex !== null && dex !== 'main';
}
